import { useState } from 'react'
import type { StrategyId } from '../../analytics/events'
import { trackStrategySelected } from '../../analytics/track'
import { HERO_RISK_NOTICE, PREVIEW_FIGURES_NOTE } from '../risk-copy'
import { MetaTraderMark } from './MetaTraderMark'

type PreviewExample = {
  id: StrategyId
  label: string
  start: string
  stake: string
  target: string
  stop: string
  payout: string
  targetPct: string
  stopPct: string
  potentialPct: string
}

/** Same illustrative figures as the Strategies payout cards. 1 lot = $10 stake. */
const PREVIEW_EXAMPLES: PreviewExample[] = [
  {
    id: 'safe',
    label: 'SAFE',
    start: '$10',
    stake: '$100',
    target: '$5',
    stop: '$5',
    payout: '$160',
    targetPct: '2.0%',
    stopPct: '1.0%',
    potentialPct: '+60%*',
  },
  {
    id: 'balance',
    label: 'BALANCE',
    start: '$10',
    stake: '$100',
    target: '$10',
    stop: '$5',
    payout: '$250',
    targetPct: '3.0%',
    stopPct: '1.0%',
    potentialPct: '+150%*',
  },
  {
    id: 'aggressive',
    label: 'AGGRESSIVE',
    start: '$10',
    stake: '$100',
    target: '$20',
    stop: '$5',
    payout: '$400',
    targetPct: '4.0%',
    stopPct: '1.0%',
    potentialPct: '+300%*',
  },
]

function BuyArrow() {
  return (
    <svg className="tl-kpi__arrow" viewBox="0 0 12 14" aria-hidden="true">
      <path d="M6 1.2 11 8H8.2V12.8H3.8V8H1L6 1.2Z" fill="currentColor" />
    </svg>
  )
}

function SellArrow() {
  return (
    <svg className="tl-kpi__arrow" viewBox="0 0 12 14" aria-hidden="true">
      <path d="M6 12.8 1 6h2.8V1.2h4.4V6H11L6 12.8Z" fill="currentColor" />
    </svg>
  )
}

export function TradeKpiPreview() {
  const [strategy, setStrategy] = useState<StrategyId>('balance')
  const example = PREVIEW_EXAMPLES.find((item) => item.id === strategy) ?? PREVIEW_EXAMPLES[1]

  return (
    <>
    <aside className="tl-kpi" aria-label="Illustrative prediction snapshot">
      <div className="tl-kpi__head">
        <p className="tl-kpi__market">
          <img className="tl-kpi__gold tl-m-only" src="/brand/gold-bar.svg" alt="" width={36} height={24} />
          Gold
          <span className="tl-kpi__dot">· </span>
          <span>XAUUSD</span>
        </p>
        <p className="tl-kpi__hint tl-d-only">Illustrative preview</p>
        <p className="tl-kpi__min tl-m-only">
          <span>Minimum Investment</span>
          <strong>$10</strong>
        </p>
      </div>

      <div className="tl-kpi__pills" role="radiogroup" aria-label="Illustrative strategy profile">
        {PREVIEW_EXAMPLES.map((item) => {
          const on = strategy === item.id
          return (
            <button
              key={item.id}
              type="button"
              role="radio"
              aria-checked={on}
              className={on ? 'is-on' : undefined}
              onClick={() => {
                setStrategy(item.id)
                trackStrategySelected(item.id, 'trade_landing')
              }}
            >
              {item.label}
            </button>
          )
        })}
      </div>

      <div className="tl-kpi__dirs" aria-hidden="true">
        <span className="tl-dir tl-dir--buy">
          <BuyArrow />
          BUY
        </span>
        <span className="tl-dir tl-dir--sell">
          <SellArrow />
          SELL
        </span>
      </div>

      <dl className="tl-kpi__box tl-d-only" aria-live="polite">
        <div className="tl-kpi__row">
          <dt>Start with</dt>
          <dd>{example.start}</dd>
        </div>
        <div className="tl-kpi__row">
          <dt>Target</dt>
          <dd>{example.target}</dd>
        </div>
        <div className="tl-kpi__row">
          <dt>Stop</dt>
          <dd>{example.stop}</dd>
        </div>
        <div className="tl-kpi__row tl-kpi__row--accent">
          <dt>Illustrative total payout on {example.stake} stake</dt>
          <dd>{example.payout}</dd>
        </div>
      </dl>

      <dl className="tl-kpi__metrics tl-m-only" aria-live="polite">
        <div>
          <dt>TARGET</dt>
          <dd>{example.targetPct}</dd>
        </div>
        <div>
          <dt>STOP</dt>
          <dd>{example.stopPct}</dd>
        </div>
        <div>
          <dt>POTENTIAL</dt>
          <dd>{example.potentialPct}</dd>
        </div>
      </dl>

      <p className="tl-kpi__example tl-d-only">Illustrative Example</p>
      <p className="tl-kpi__note tl-d-only">{PREVIEW_FIGURES_NOTE}</p>
      <p className="tl-kpi__note tl-m-only">Illustrative preview. Outcomes are not guaranteed.</p>

      <div className="tl-kpi__exec">
        <MetaTraderMark className="tl-kpi__mt5" />
        <p>Execution through MetaTrader 5</p>
      </div>
    </aside>
    <p className="tl-m-risk tl-m-only">
      {HERO_RISK_NOTICE} 18+
    </p>
    </>
  )
}
